import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Avatar
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { rawApiClient } from '../../services/api';
import { useClientUiSettings } from '../../hooks/useClientUiSettings';

/**
 * "About this AI" dialog.
 *
 * Surfaces the assistant-not-therapy framing, the modalities the persona's
 * specialist modules draw on, the live-video privacy note, and the
 * "not-replacement" disclaimer — all editable copy from PlatformSettings.clientUi
 * (admin-controlled).
 *
 * Logs ABOUT_THIS_AI_OPENED on open for the engagement metric defined in the
 * Meeting 3 implementation plan.
 *
 * See docs/canaday-youth-outreach/04-implementation-spec.md section 4.7.
 */
const AboutThisAiDialog = ({
  open,
  onClose,
  personaId,
  personaKey,
  personaName,
  personaAvatarUrl = null,
  personaRoleLabel = null,
  underlyingPersonaName = null,
  onChangeAppearance = null
}) => {
  const { aboutThisAi = {}, roleLabel: resolvedRoleLabel = 'AI Therapist' } = useClientUiSettings();
  const displayRoleLabel = personaRoleLabel || resolvedRoleLabel;
  const [modules, setModules] = useState([]);

  const initials = (personaName || personaKey || '')
    .split(/[\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase())
    .join('') || '·';

  useEffect(() => {
    if (!open) return;
    const token = typeof window !== 'undefined' ? window.localStorage?.getItem('token') : null;

    // Fetch modules for the active persona, if any. The server endpoint logs
    // ABOUT_THIS_AI_OPENED as a side effect — engagement metric.
    if ((!personaId && !personaKey) || !token) {
      setModules([]);
      return;
    }
    const params = personaId ? { personaId } : { personaKey };
    rawApiClient
      .get('/client/ui-settings/modules', {
        params,
        headers: { 'x-auth-token': token }
      })
      .then((res) => setModules(Array.isArray(res.data) ? res.data : []))
      .catch(() => setModules([]));
  }, [open, personaId, personaKey]);

  const headline = aboutThisAi.headline || 'About this AI';
  const notReplacement =
    aboutThisAi.notReplacementLine ||
    'The AI is an assistant to your therapy. Your therapy is with your human therapist.';
  const liveVideo = aboutThisAi.liveVideoLine || '';
  const body = aboutThisAi.body || '';

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoOutlinedIcon color="primary" />
        {headline}
      </DialogTitle>
      <DialogContent>
        {/* Profile-style header — avatar prominent on the left so the artwork
            (including any subtle AI cues in the image) is large enough to
            read. Name, role chip, "Powered by ..." and Change-appearance
            stack on the right, left-aligned for a compact, scannable layout.
            On narrow screens it falls back to the original column form. */}
        {personaName && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              textAlign: { xs: 'center', sm: 'left' },
              gap: 2,
              mb: 3,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            {personaAvatarUrl ? (
              <Avatar
                src={personaAvatarUrl}
                alt={personaName}
                sx={{ width: 140, height: 140, boxShadow: 2, flexShrink: 0 }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 140,
                  height: 140,
                  bgcolor: 'primary.light',
                  fontSize: '2.6rem',
                  boxShadow: 2,
                  flexShrink: 0
                }}
              >
                {initials}
              </Avatar>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', sm: 'flex-start' },
                gap: 0.75,
                minWidth: 0,
                flex: 1
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {personaName}
              </Typography>
              <Chip
                size="small"
                label={displayRoleLabel}
                color="primary"
                variant="outlined"
              />
              {underlyingPersonaName && (
                <Typography variant="caption" color="text.secondary">
                  Powered by {underlyingPersonaName}
                </Typography>
              )}
              {onChangeAppearance && (
                <Button
                  size="small"
                  onClick={() => { onClose(); onChangeAppearance(); }}
                  sx={{ textTransform: 'none', alignSelf: { xs: 'center', sm: 'flex-start' }, px: 0 }}
                >
                  Change appearance
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Typography variant="body1" paragraph>
          {notReplacement}
        </Typography>

        {body && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {body}
          </Typography>
        )}

        {modules.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Approaches this AI draws on
            </Typography>
            <List dense disablePadding>
              {modules.map((m) => (
                <ListItem key={m.key} sx={{ alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={m.name}
                    secondary={m.publicSummary}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              ))}
            </List>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              The AI draws on ideas from these approaches. It does not deliver any of them itself.
            </Typography>
          </>
        )}

        {liveVideo && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {liveVideo}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AboutThisAiDialog;
